import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { consentPrimaryClass, consentSecondaryClass } from "./consentStyles";

/**
 * "Personalizează", and the "Cookie settings" link in the footer: one switch
 * per optional category, with what it does and who receives what.
 *
 * The marketing switch starts where the visitor's standing answer is, and off
 * when there is none — a pre-ticked box is not consent (CJEU C-673/17,
 * Planet49). Closing without saving changes nothing; the banner, if it was up,
 * comes back.
 */
const CookiePreferencesDialog = ({
  open,
  marketing: savedMarketing,
  onClose,
  onSave,
  onAcceptAll,
  onRejectOptional,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const closeButtonRef = useRef(null);
  const [marketing, setMarketing] = useState(savedMarketing);

  // Every opening starts from the answer actually stored, not from a switch
  // flipped and then abandoned last time.
  useEffect(() => {
    if (open) setMarketing(savedMarketing);
  }, [open, savedMarketing]);

  useEffect(() => {
    if (!open) return undefined;

    // Focus in, and back to the link that opened it on the way out.
    const previouslyFocused = document.activeElement;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="cookie-preferences-overlay fixed inset-0 z-100 flex items-center justify-center overflow-y-auto bg-ink-900/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        className="cookie-preferences flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-card bg-white shadow-card-hover"
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-ink-200 px-5 py-3 sm:px-6">
          <h2 id={titleId} className="text-fluid-lg font-bold tracking-tight text-ink-900">
            {t("CookieConsent.dialogTitle")}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={t("CookieConsent.close")}
            className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-ink-500 transition hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          >
            <i className="bi bi-x text-2xl" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5 sm:px-6">
          <p className="text-fluid-sm leading-relaxed text-ink-600">
            {t("CookieConsent.dialogIntro")}
          </p>

          <div className="cookie-category-essential rounded-lg border border-ink-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-ink-900">
                {t("CookieConsent.essentialTitle")}
              </h3>
              <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-800">
                {t("CookieConsent.essentialAlwaysOn")}
              </span>
            </div>
            <p className="mt-2 text-fluid-sm leading-relaxed text-ink-600">
              {t("CookieConsent.essentialDescription")}
            </p>
          </div>

          <div className="cookie-category-marketing rounded-lg border border-ink-200 p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold text-ink-900">
                {t("CookieConsent.marketingTitle")}
              </h3>
              <button
                type="button"
                role="switch"
                aria-checked={marketing}
                aria-label={t("CookieConsent.marketingToggle")}
                onClick={() => setMarketing((value) => !value)}
                className="cookie-marketing-switch -my-2 -mr-1.5 flex h-11 w-14 shrink-0 cursor-pointer items-center justify-center rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              >
                <span
                  aria-hidden="true"
                  className={`relative inline-flex h-6 w-11 rounded-full transition ${
                    marketing ? "bg-brand-600" : "bg-ink-300"
                  }`}
                >
                  <span
                    className={`absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow transition ${
                      marketing ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </span>
              </button>
            </div>
            <p className="mt-2 text-fluid-sm leading-relaxed text-ink-600">
              {t("CookieConsent.marketingDescription")}
            </p>
          </div>

          <Link
            to="/cookies"
            onClick={onClose}
            className="inline-block text-fluid-sm font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
          >
            {t("CookieConsent.policyLink")}
          </Link>
        </div>

        <div className="grid shrink-0 grid-cols-1 gap-2 border-t border-ink-200 px-5 py-4 sm:px-6 md:grid-cols-3">
          <button
            type="button"
            className={`cookie-accept-all ${consentPrimaryClass}`}
            onClick={onAcceptAll}
          >
            {t("CookieConsent.acceptAll")}
          </button>
          <button
            type="button"
            className={`cookie-save ${consentSecondaryClass}`}
            onClick={() => onSave({ marketing })}
          >
            {t("CookieConsent.save")}
          </button>
          <button
            type="button"
            className={`cookie-reject-optional ${consentSecondaryClass}`}
            onClick={onRejectOptional}
          >
            {t("CookieConsent.rejectOptional")}
          </button>
        </div>
      </div>
    </div>
  );
};

CookiePreferencesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The standing answer; false when there is none. */
  marketing: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onAcceptAll: PropTypes.func.isRequired,
  onRejectOptional: PropTypes.func.isRequired,
};

export default CookiePreferencesDialog;
